/**
 * Role gate for a role-scoped route tree.
 *
 * Must be nested INSIDE a RequireAuth branch: it assumes authentication has
 * already been established and only decides whether THIS role belongs in THIS
 * subtree. A user with the wrong role is sent to their own home rather than to
 * the login page — they are legitimately logged in, just in the wrong place.
 *
 * The Flutter router has no equivalent: its shell routes are not role-gated at
 * all, so an authenticated tenant who types `/landlord` gets the landlord shell
 * (the SCREENS then filter by role, and the API scopes every query server-side,
 * which is what actually protects the data). Gating the subtree here is a
 * deliberate improvement — a clearer failure mode than a dashboard that renders
 * empty because every request behind it 403s. This is a UX guard ONLY; the real
 * authorisation boundary is the backend's RBAC middleware, which this cannot
 * and does not replace.
 */
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { ROUTES, homePathForRole } from '../paths';
import type { UserRole } from '../../types/user';

export interface RequireRoleProps {
  allowed: readonly UserRole[];
}

export function RequireRole({ allowed }: RequireRoleProps): React.JSX.Element {
  const { user } = useAuth();

  if (user === null) {
    return <Navigate to={ROUTES.login} replace />;
  }

  if (!allowed.includes(user.role)) {
    return <Navigate to={homePathForRole(user.role)} replace />;
  }

  return <Outlet />;
}

export default RequireRole;
